/* eslint-disable max-lines-per-function */
import type {
  KLineData,
  ExtendLineSeries,
  ExtendBarSeries,
  ExtendTagSeries,
  Figure,
  StyleConfig,
  StateItem,
  CandleStatus,
  ProcessCandlesParams,
  ProcessExtendDataParams
} from '../helper/hxKLine';

// 扩展KLineData类型，添加缺失的属性
interface EnhancedKLineData extends KLineData {
  index?: number;
  pre?: number;
}

// 添加HXKlineChart类型声明
declare global {
  interface Window {
    HXKlineChart: {
      registerOverlay: (config: Record<string, unknown>) => void;
      utils: {
        calcTextWidth: (text: string, size: number) => number;
      };
    };
    HXKline: unknown;
  }
}

// 柱状图数据项类型
interface BarDataItem {
  timestamp: number;
  value: number;
  dataIndex: number;
}

export function validateHXKlineComponents() {
  if (!window.HXKlineChart || !window.HXKline) {
    throw new Error(
      'HXKlineChart and HXKline components are not detected. Please check whether the resources are correctly imported (K-line resources must be imported before AIGCDataVis)'
    );
  }
}

// 辅助函数：获取可见的柱状图数据
function getVisibleBarData(
  extendData: ExtendBarSeries['extendData'],
  visibleDataList: Array<{ data: KLineData }>
): BarDataItem[] {
  const { barData } = extendData;
  const visibleStart = visibleDataList[0].data;
  const visibleEnd = visibleDataList.at(-1)?.data;

  if (!visibleEnd) {
    return [];
  }

  return barData.filter(item => {
    return item.timestamp >= visibleStart.timestamp && item.timestamp <= visibleEnd.timestamp;
  });
}

// 辅助函数：绘制柱状图图形
function drawBarFigures(
  overlayDataVisible: BarDataItem[],
  extendData: ExtendBarSeries['extendData'],
  yAxis: {
    convertToPixel: (value: number) => number;
    _extremum: { min: number; range: number };
  },
  bounding: { height: number },
  barSpace: { bar: number }
): Figure[] {
  const { gap, color, highlightColor, highlightIndex } = extendData;

  // 计算最大值和范围
  let overlayDataVisibleMax = overlayDataVisible[0].value;
  const overlayDataVisibleMin = 0;

  overlayDataVisible.forEach(element => {
    if (element.value > overlayDataVisibleMax) {
      overlayDataVisibleMax = element.value;
    }
  });

  let overlayDataVisibleRange = overlayDataVisibleMax - overlayDataVisibleMin;
  extendData?.onUpdateLeftYAxis?.({
    min: overlayDataVisibleMin,
    max: overlayDataVisibleMax
  });

  // 扩大绘图点数据的范围，以实现柱子不贴上边
  overlayDataVisibleMax += overlayDataVisibleRange * gap;
  overlayDataVisibleRange = overlayDataVisibleMax - overlayDataVisibleMin;
  const { min, range } = yAxis._extremum;

  const suitableYAxis = (value: number): number => {
    const timeSize = range / overlayDataVisibleRange;
    const yValue = (value - overlayDataVisibleMin) * timeSize;
    return min + yValue;
  };

  // 绘制柱状图
  const drawBarDataList: Figure[] = [];
  overlayDataVisible.forEach(item => {
    const x1 = extendData.hxKLine.convertToPixel(
      { timestamp: item.timestamp },
      { paneId: 'candle_pane' }
    ).x;
    const width = barSpace.bar > 5 ? barSpace.bar : 5;
    const y1 = yAxis.convertToPixel(suitableYAxis(item.value));
    const isHighlight = item.dataIndex === highlightIndex;

    drawBarDataList.push({
      key: item.dataIndex,
      type: 'rect',
      attrs: {
        x: x1 - width * 0.4,
        y: y1,
        width: width * 0.8,
        height: bounding.height - y1
      },
      styles: {
        color: isHighlight ? highlightColor : color
      }
    });
  });

  return drawBarDataList;
}

function truncateText(
  str: string,
  containerWidth: number,
  fontSize: number,
  measureText: (text: string, size: number) => number
): string {
  if (containerWidth <= 0 || str.length === 0) {
    return '';
  }

  // 测量基础数据
  const ellipsis = '...';
  const ellipsisWidth = measureText(ellipsis, fontSize);
  const fullWidth = measureText(str, fontSize);

  // 完整显示检查
  if (fullWidth <= containerWidth) {
    return str;
  }

  // 检查是否只能放下一个字符
  const firstCharWidth = measureText(str.substring(0, 1), fontSize);
  if (firstCharWidth <= containerWidth && firstCharWidth + ellipsisWidth > containerWidth) {
    return str.substring(0, 1);
  }

  // 二分查找最佳截断位置
  let low = 1;
  let high = str.length - 1;
  let best = -1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const currentStr = str.substring(0, mid);
    const currentWidth = measureText(currentStr, fontSize);

    if (currentWidth + ellipsisWidth <= containerWidth) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return best === -1 ? '' : str.substring(0, best) + ellipsis;
}

const getStateStyles = ({ isActive, isHover }: StateItem, styles: StyleConfig) => {
  const {
    activeBackground,
    hoverBackground,
    defaultBackground,
    activeBorder,
    hoverBorder,
    defaultBorder
  } = styles;
  const state = {
    background: defaultBackground,
    border: defaultBorder
  };
  if (isActive) {
    state.background = activeBackground;
    state.border = activeBorder;
  } else if (isHover) {
    state.background = hoverBackground;
    state.border = hoverBorder;
  }
  return state;
};

const getCandleStatus = (close: number, open: number, pre: number): CandleStatus => {
  if (close !== open) {
    return close > open ? 'up' : 'down';
  }
  if (close !== pre) {
    return close > pre ? 'up' : 'down';
  }
  return 'neutral';
};

function processExtendData({
  extendData,
  dataList,
  xAxis,
  containerHeight,
  halfGapBar,
  figures,
  klineDataListForPaint
}: ProcessExtendDataParams): void {
  const titleContainerMargin = 2;
  const titleContainerPadding = 4;

  extendData.forEach(item => {
    const { beginTimestamp, endTimestamp, titleList, textSize } = item;
    const styleConfig: StyleConfig = {
      activeBackground: item.activeBackgroundColor,
      hoverBackground: item.hoverBackgroundColor,
      defaultBackground: item.backgroundColor,
      activeBorder: item.activeBorderColor,
      hoverBorder: item.hoverBorderColor,
      defaultBorder: item.borderColor
    };
    const beginIndex = dataList.findIndex(d => d.timestamp === beginTimestamp);
    let endIndex = dataList.findIndex(d => d.timestamp === endTimestamp);
    const { background, border } = getStateStyles(item as unknown as StateItem, styleConfig);

    // 如果只有起始时间找到但结束时间未找到，则认为结束时间为最后一个数据项
    if (beginIndex !== -1 && endIndex === -1) {
      endIndex = dataList.length - 1;
    }
    // 如果两个索引均无效，则跳过该区域
    if (beginIndex === -1 && endIndex === -1) {
      return;
    }

    const startX = xAxis.convertToPixel(beginIndex) - halfGapBar;
    const endX = xAxis.convertToPixel(endIndex) + halfGapBar;

    // 将该区域内的 kline 数据存入绘制数组中
    klineDataListForPaint.push(...dataList.slice(beginIndex, endIndex + 1));

    // 添加覆盖区域的背景矩形
    figures.push({
      key: item.id,
      type: 'rect',
      attrs: {
        x: startX,
        y: 0,
        width: endX - startX,
        height: containerHeight
      },
      styles: {
        style: 'stroke_fill',
        color: background,
        borderColor: border
      }
    });

    // 计算标题区域高度：文本高度 + 内边距
    const titleHeight = textSize + titleContainerPadding * 2;
    titleList.forEach((concept, idx) => {
      // 为每个概念创建必要的属性
      const conceptRect = {
        styles: concept.rect.styles
      };

      const conceptText = {
        name: concept.text.name,
        styles: concept.text.styles
      };

      // 标题 Y 坐标计算：容器高度 - (标题高度+间距)*序号
      const titleY = containerHeight - (titleHeight + titleContainerMargin) * (idx + 1);
      // 计算标题容器的宽度，至少为 0
      const titleContainerWidth = Math.max(endX - startX - titleContainerMargin * 2, 0);

      // 添加标题背景矩形
      figures.push({
        key: `${item.id}titleRect${idx}`,
        type: 'rect',
        attrs: {
          x: startX + titleContainerMargin,
          y: titleY,
          width: titleContainerWidth,
          height: titleHeight
        },
        styles: conceptRect.styles,
        ignoreEvent: true
      });

      // 限制文本宽度（扣除容器左右 margin 和 padding）
      const textWidthLimit =
        titleContainerWidth - titleContainerMargin * 2 - titleContainerPadding * 2;
      const text = truncateText(
        conceptText.name,
        textWidthLimit,
        textSize,
        window.HXKlineChart.utils.calcTextWidth
      );
      // 添加标题文本
      figures.push({
        key: `${item.id}text${idx}`,
        type: 'text',
        attrs: {
          x: startX + titleContainerMargin + titleContainerPadding,
          y: titleY - titleContainerPadding * 2 + textSize / 2,
          text
        },
        styles: conceptText.styles,
        ignoreEvent: true
      });
    });
  });
}

function processCandles({
  klineDataList,
  xAxis,
  yAxis,
  halfGapBar,
  gapBar,
  defaultStyles,
  figures
}: ProcessCandlesParams): void {
  const {
    upColor,
    upBorderColor,
    upWickColor,
    downColor,
    downBorderColor,
    downWickColor,
    noChangeColor,
    noChangeBorderColor,
    noChangeWickColor
  } = defaultStyles.candle.bar;

  klineDataList.forEach(item => {
    const { open, high, low, close, index, pre, timestamp } = item;
    if (index === undefined || pre === undefined) {
      return;
    }

    const x = xAxis.convertToPixel(index);
    const openY = yAxis.convertToPixel(open);
    const highY = yAxis.convertToPixel(high);
    const lowY = yAxis.convertToPixel(low);
    const closeY = yAxis.convertToPixel(close);

    // 根据开盘和收盘价格（或与前一价格比较）确定颜色
    const COLOR_CONFIG = {
      up: {
        body: upColor,
        border: upBorderColor,
        wick: upWickColor
      },
      down: {
        body: downColor,
        border: downBorderColor,
        wick: downWickColor
      },
      neutral: {
        body: noChangeColor,
        border: noChangeBorderColor,
        wick: noChangeWickColor
      }
    };
    // 3. 获取颜色配置
    const {
      body: bodyColor,
      border: borderColor,
      wick: wickColor
    } = COLOR_CONFIG[getCandleStatus(close, open, pre)];

    // 添加蜡烛矩形（实体）
    figures.push({
      key: `${timestamp}rect`,
      type: 'rect',
      attrs: {
        x: x - halfGapBar,
        y: Math.min(openY, closeY),
        width: gapBar,
        height: Math.abs(closeY - openY) || 1
      },
      styles: {
        color: bodyColor,
        borderColor
      }
    });

    // 添加蜡烛线（影线）
    figures.push({
      key: `${timestamp}line`,
      type: 'line',
      attrs: {
        coordinates: [
          { x, y: highY },
          { x, y: lowY }
        ]
      },
      styles: {
        color: wickColor
      }
    });
  });
}

/**
 * 获取最后一个有效K线点
 */
function getLastValidKLinePoint(
  dataList: Array<Record<string, number>>,
  extendData: ExtendLineSeries['extendData']
) {
  const firstInvalidKLinePointIndex = dataList.findIndex(item => {
    return Number.isNaN(+item.close);
  });
  const isFull = firstInvalidKLinePointIndex === -1;

  // 确保lastKLinePoint始终有值
  return isFull
    ? extendData.data.at(-1) || extendData.data[0]
    : dataList[Math.max(firstInvalidKLinePointIndex - 1, 0)];
}

/**
 * 获取最后一个点的索引
 */
function getLastPointIndex(
  lastKLinePoint: Record<string, number>,
  extendData: ExtendLineSeries['extendData']
) {
  // 找出最后一个非NaN的点，作为折线的终点
  const lastedPointIndex = extendData.data.findIndex(item => {
    return item.timestamp >= lastKLinePoint.timestamp;
  });
  // 如果找不到，说明最后一个点在数据列表的最后，取最后一个点
  return lastedPointIndex === -1 ? extendData.data.length - 1 : lastedPointIndex;
}

// 定义具有x属性的类型
interface LinePlayDataItem extends Record<string, number> {
  dataIndex: number;
  x: number;
}

/**
 * 创建折线图数据列表
 */
function createLinePlayDataList(
  coordinates: Array<Record<string, number>>,
  extendData: ExtendLineSeries['extendData'],
  lastedPointIndex: number,
  isFull: boolean
) {
  // 添加类型检查，确保包含x和dataIndex属性
  return coordinates
    .map((item, index) => {
      if (index < extendData.data.length) {
        return {
          ...item,
          dataIndex: extendData.data[index].dataIndex
        };
      }
      return null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .slice(0, isFull ? lastedPointIndex + 1 : lastedPointIndex);
}

/**
 * 创建tooltip触发区域
 */
function createTooltipRects(
  lingPlayDataList: LinePlayDataItem[],
  barSpace: { bar: number },
  bounding: { height: number }
) {
  const rects: unknown[] = [];

  lingPlayDataList.forEach(item => {
    if (typeof item.x !== 'number') {
      return;
    }

    const x1 = item.x;
    const width = barSpace.bar > 5 ? barSpace.bar : 5;

    rects.push({
      key: item.dataIndex,
      type: 'rect',
      attrs: {
        x: x1 - width * 0.4,
        y: 0,
        width: width * 0.8,
        height: bounding.height
      },
      styles: {
        color: 'rgba(255, 0, 0, 0.0)'
      }
    });
  });

  return rects;
}

if (window.HXKlineChart) {
  /**
   * 扩展一个折线系列
   */
  // 注册折线图覆盖物
  window.HXKlineChart.registerOverlay({
    name: 'line',
    lock: true,
    // @ts-ignore
    createPointFigures: (config: Record<string, unknown>) => {
      const { overlay, coordinates, dataList, bounding, barSpace } = config as {
        overlay: { extendData: ExtendLineSeries['extendData'] };
        coordinates: Array<Record<string, number>>;
        dataList: Array<Record<string, number>>;
        bounding: { height: number };
        barSpace: { bar: number };
      };
      const { extendData } = overlay;

      const firstInvalidKLinePointIndex = dataList.findIndex(item => {
        return Number.isNaN(+item.close);
      });
      const isFull = firstInvalidKLinePointIndex === -1;

      const lastKLinePoint = getLastValidKLinePoint(dataList, extendData);

      if (!lastKLinePoint) {
        return [];
      }

      const lastedPointIndex = getLastPointIndex(lastKLinePoint, extendData);

      const lingPlayDataList = createLinePlayDataList(
        coordinates,
        extendData,
        lastedPointIndex,
        isFull
      );

      if (lastedPointIndex > -1 && lingPlayDataList.length >= 2) {
        // * 利用占位柱图来触发 tooltip，解决折线图无法 tooltip 交互问题
        const rects = extendData.enableTooltip
          ? createTooltipRects(lingPlayDataList as LinePlayDataItem[], barSpace, bounding)
          : [];

        return [
          {
            type: 'line',
            attrs: {
              coordinates: lingPlayDataList
            },
            styles: {
              color: extendData?.color ?? 'rgba(255, 0, 0, 0.5)',
              size: extendData?.size ?? 2
            },
            ignoreEvent: true
          },
          ...rects
        ];
      }

      return [];
    },
    // @ts-ignore
    onMouseEnter: (event: {
      overlay: { extendData: ExtendLineSeries['extendData'] };
      x: number;
      y: number;
      figureKey: number | string;
    }) => {
      const { extendData } = event.overlay;
      extendData?.onMouseEnter?.({
        x: event.x,
        y: event.y,
        dataIndex: Number(event.figureKey)
      });
    },
    // @ts-ignore
    onMouseLeave: (event: {
      overlay: { extendData: ExtendLineSeries['extendData'] };
      x: number;
      y: number;
      figureKey: number | string;
    }) => {
      const { extendData } = event.overlay;
      extendData?.onMouseLeave?.({
        x: event.x,
        y: event.y,
        dataIndex: Number(event.figureKey)
      });
    }
  });

  /**
   * 扩展一个柱状系列
   */
  // 注册柱状图覆盖物
  window.HXKlineChart.registerOverlay({
    name: 'bar',
    lock: true,
    createPointFigures: (params: Record<string, unknown>) => {
      const { overlay, bounding, yAxis, barSpace } = params as {
        overlay: { extendData: ExtendBarSeries['extendData'] };
        bounding: { height: number };
        yAxis: {
          convertToPixel: (value: number) => number;
          _extremum: { min: number; range: number };
        };
        barSpace: { bar: number };
      };
      const { extendData } = overlay;
      const visibleDataList = extendData.hxKLine.getVisibleDataList();

      // 如果没有可见数据，则返回空数组
      if (!visibleDataList.length || !visibleDataList.at(-1)?.data) {
        return [];
      }

      // 获取可见的柱状图数据
      const overlayDataVisible = getVisibleBarData(extendData, visibleDataList);

      // 如果没有可见的柱状图数据，则返回空数组
      if (overlayDataVisible.length === 0) {
        return [];
      }

      // 计算Y轴刻度并绘制柱状图
      return drawBarFigures(overlayDataVisible, extendData, yAxis, bounding, barSpace);
    },
    // @ts-ignore
    onMouseEnter: (event: {
      overlay: { extendData: ExtendBarSeries['extendData'] };
      x: number;
      y: number;
      figureKey: number | string;
    }) => {
      const { extendData } = event.overlay;
      extendData?.onMouseEnter?.({
        x: event.x,
        y: event.y,
        dataIndex: Number(event.figureKey)
      });

      // 创建一个副本以避免直接修改原对象
      const newExtendData = { ...extendData, highlightIndex: Number(event.figureKey) };
      // 使用类型断言避免TypeScript错误
      const overlayWithMutableData = event.overlay as { extendData: ExtendBarSeries['extendData'] };
      overlayWithMutableData.extendData = newExtendData;
    },
    // @ts-ignore
    onMouseLeave: (event: {
      overlay: { extendData: ExtendBarSeries['extendData'] };
      x: number;
      y: number;
      figureKey: number | string;
    }) => {
      const { extendData } = event.overlay;
      extendData?.onMouseLeave?.({
        x: event.x,
        y: event.y,
        dataIndex: Number(event.figureKey)
      });

      // 创建一个副本以避免直接修改原对象
      const newExtendData = { ...extendData, highlightIndex: undefined };
      // 使用类型断言避免TypeScript错误
      const overlayWithMutableData = event.overlay as { extendData: ExtendBarSeries['extendData'] };
      overlayWithMutableData.extendData = newExtendData;
    }
  });

  /**
   * 扩展一个标签系列
   */
  // 注册标签覆盖物
  window.HXKlineChart.registerOverlay({
    name: 'tag',
    lock: true,
    // 创建点对应的图形信息：矩形背景，矩形文本背景、单行文本
    createPointFigures: (params: Record<string, unknown>) => {
      const { overlay, bounding, barSpace, xAxis, yAxis, dataList, defaultStyles } = params as {
        overlay: { extendData: ExtendTagSeries['extendData'] };
        bounding: { height: number };
        barSpace: { halfGapBar: number; gapBar: number };
        xAxis: { convertToPixel: (index: number) => number };
        yAxis: { convertToPixel: (value: number) => number };
        dataList: KLineData[];
        defaultStyles: Record<string, unknown>;
      };
      const enhancedDataList = dataList.map((item, index) => ({
        ...item,
        index
      }));

      const { extendData } = overlay;
      const containerHeight = bounding.height;
      const { halfGapBar, gapBar } = barSpace;
      const klineDataListForPaint: KLineData[] = [];
      const res: Figure[] = [];
      if (extendData?.length > 0) {
        processExtendData({
          extendData,
          dataList: enhancedDataList,
          xAxis,
          containerHeight,
          halfGapBar,
          figures: res,
          klineDataListForPaint
        });
      }
      processCandles({
        klineDataList: klineDataListForPaint as EnhancedKLineData[],
        xAxis,
        yAxis,
        halfGapBar,
        gapBar,
        defaultStyles: defaultStyles as ProcessCandlesParams['defaultStyles'],
        figures: res
      });
      return res;
    },
    // 点击回调事件
    onClick: (event: {
      overlay: { extendData: ExtendTagSeries['extendData'] };
      figureKey: number | string;
    }) => {
      const { extendData } = event.overlay;
      const newExtendData = extendData.map(item => ({
        ...item,
        isActive: item.id === event.figureKey
      }));

      // 使用类型断言避免TypeScript错误
      const overlayWithMutableData = event.overlay as { extendData: ExtendTagSeries['extendData'] };
      overlayWithMutableData.extendData = newExtendData;

      const target = newExtendData.find(item => item.id === event.figureKey);
      if (typeof target === 'undefined') {
        return;
      }

      target?.onClick?.({
        id: Number(event.figureKey)
      });
    },
    // 鼠标移入事件
    onMouseEnter: (event: {
      overlay: { extendData: ExtendTagSeries['extendData'] };
      figureKey: number | string;
    }) => {
      const { extendData } = event.overlay;
      const newExtendData = [...extendData];
      const targetIndex = newExtendData.findIndex(item => item.id === event.figureKey);

      if (targetIndex === -1) {
        return;
      }

      newExtendData[targetIndex] = {
        ...newExtendData[targetIndex],
        isHover: true
      };

      // 使用类型断言避免TypeScript错误
      const overlayWithMutableData = event.overlay as { extendData: ExtendTagSeries['extendData'] };
      overlayWithMutableData.extendData = newExtendData;

      newExtendData[targetIndex]?.onMouseEnter?.({
        id: Number(event.figureKey)
      });
    },
    // 鼠标移出事件
    onMouseLeave: (event: {
      overlay: { extendData: ExtendTagSeries['extendData'] };
      figureKey: number | string;
    }) => {
      const { extendData } = event.overlay;
      const newExtendData = [...extendData];
      const targetIndex = newExtendData.findIndex(item => item.id === event.figureKey);

      if (targetIndex === -1) {
        return;
      }

      newExtendData[targetIndex] = {
        ...newExtendData[targetIndex],
        isHover: false
      };

      // 使用类型断言避免TypeScript错误
      const overlayWithMutableData = event.overlay as { extendData: ExtendTagSeries['extendData'] };
      overlayWithMutableData.extendData = newExtendData;

      newExtendData[targetIndex]?.onMouseLeave?.({
        id: Number(event.figureKey)
      });
    }
  });
}
/**
 * 根据 k 线数据集调整给定数据集的时间戳
 *
 * ! 因为在行情图上叠加其它图形，必须要求其它图形的数据点和 k 线的数据点是同一时间戳
 */
export function adaptData<D extends { timestamp: number }>(
  klineData: KLineData[],
  targetData: D[]
) {
  const times = klineData.map(item => item.timestamp);
  const result = [] as { timestamp: number; __originData: D }[];

  targetData.forEach(item => {
    let index = times.findLastIndex(timestamp => item.timestamp >= timestamp);

    if (index === -1) {
      index = item.timestamp < times[0] ? 0 : times.length - 1;
    }

    result.push({
      timestamp: times[index],
      __originData: item
    });
  });

  return result;
}

/**
 * 根据给定时间戳寻找 k 线数据索引
 */
export function findIndexByTimestamp(
  klineData: KLineData[],
  timestamp: number,
  find: 'start' | 'end'
) {
  const timestampTemp = Math.min(
    Math.max(timestamp, klineData[0].timestamp),
    klineData[klineData.length - 1].timestamp
  );

  return find === 'start'
    ? klineData.findIndex(item => item.timestamp >= timestampTemp)
    : klineData.findLastIndex(item => item.timestamp <= timestampTemp);
}
